/**
 * @module utilities
 */

import { addToastMessage } from '../components/toast';
import { addToCompareHandler } from '../components/quick-view';

/**
 * Runs a callback function after the DOM has finished loading
 * @param {genericCallback} [callback] - contains code to be executed after
 */

export function docReady(callback) {
  if (document.readyState !== 'loading') {
    callback();
  } else {
    document.addEventListener('DOMContentLoaded', callback);
  }
}


/**
 * A useful alias for querySelectorAll
 * @param {(string|Element)} target - The selector or target element
 * @param {string} [selector] - The selector to query against the target element
 */

export function _(target, selector) {
  if (typeof target === 'object') {
    return target.querySelectorAll(selector);
  }
  return document.querySelectorAll(target);
}


/**
 * Toggles a class on an element
 * @param {string} [className] - The class to toggle
 */

export function toggleClass(el, className) {
  if (el.classList) {
    el.classList.toggle(className);
  } else {
    const classes = el.className.split(' ');
    const existingIndex = classes.indexOf(className);
    if (existingIndex >= 0) {
      classes.splice(existingIndex, 1);
    } else {
      classes.push(className);
    }
    el.className = classes.join(' ');
  }
}


/**
 * Ascertains if class exists on element
 * @param {Element} [el] - The element to target
 * @param {string} [selector] - The class to check for
 */

export function hasClass(el, selector) {
  const className = ` ${selector} `;
  if ((` ${el.className} `).replace(/[\n\t]/g, ' ').indexOf(className) > -1) {
    return true;
  }
  return false;
}

export const isLocalhost = () => {
  return ( window.location.hostname === 'localhost' || window.location.hostname === 'bs-local');
}

export const isMobile = window.innerWidth < 768;

export const copyTextToClipboard = async (text = '') => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
}

export const googleMapStyles = [
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#e9e9e9"
      },
      {
        "lightness": 17
      }
    ]
  },
  {
    "featureType": "landscape",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#f5f5f5"
      },
      {
        "lightness": 20
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.fill",
    "stylers": [
      {
        "color": "#ffffff"
      },
      {
        "lightness": 17
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.stroke",
    "stylers": [
      {
        "color": "#ffffff"
      },
      {
        "lightness": 29
      },
      {
        "weight": 0.2
      }
    ]
  },
  {
    "featureType": "road.arterial",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#ffffff"
      },
      {
        "lightness": 18
      }
    ]
  },
  {
    "featureType": "road.local",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#ffffff"
      },
      {
        "lightness": 16
      }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#f5f5f5"
      },
      {
        "lightness": 21
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#dedede"
      },
      {
        "lightness": 21
      }
    ]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "visibility": "on"
      },
      {
        "color": "#ffffff"
      },
      {
        "lightness": 16
      }
    ]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "saturation": 36
      },
      {
        "color": "#333333"
      },
      {
        "lightness": 40
      }
    ]
  },
  {
    "elementType": "labels.icon",
    "stylers": [
      {
        "visibility": "off"
      }
    ]
  },
  {
    "featureType": "transit",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#f2f2f2"
      },
      {
        "lightness": 19
      }
    ]
  },
  {
    "featureType": "administrative",
    "elementType": "geometry.fill",
    "stylers": [
      {
        "color": "#fefefe"
      },
      {
        "lightness": 20
      }
    ]
  },
  {
    "featureType": "administrative",
    "elementType": "geometry.stroke",
    "stylers": [
      {
        "color": "#fefefe"
      },
      {
        "lightness": 17
      },
      {
        "weight": 1.2
      }
    ]
  }
]

export const getUnitType = (unitType) => {
  return unitType === 0 ? 'Studio' : `${unitType}bd`
}

export const googleMapMarkerIcon = {
  point: 'data:image/svg+xml;charset=UTF-8;base64,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',
  activePoint: 'data:image/svg+xml;charset=UTF-8;base64,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',
}
const copyAddress = (e) => {
  const address = e.currentTarget.dataset.value;
  e.stopPropagation();
  e.preventDefault();
  if (address) {
    copyTextToClipboard(address.split('<br />').join(' '));
  }
  addToastMessage({
    body: 'Community address copied',
  });
}

const addToCompareOptionClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  try {
    let item = e.currentTarget.closest('.item');
    let imgSrc;
    let nameText;
    if (!item) {
      const id = e.currentTarget.dataset.id;
      if (id) {
        item = document.querySelector(`.communities-list .item[data-id="${id}"]`);
      }
    }
    if (item) {
      const imagesWrapper = item.querySelector('.images-wrapper');
      const imgEl = imagesWrapper && imagesWrapper.querySelector('img');
      const nameEl = item.querySelector('.community-name');
      imgSrc = imgEl.getAttribute('src');
      nameText = nameEl.innerHTML;
    }

    addToCompareHandler(e, imgSrc, nameText, 'right');

  } catch (e) {
    console.error(e);
  }
}

export const toggleEl = (event) => {
  const botao = event.currentTarget;
  const dropdown = botao.querySelector('.other-actions-dropdown-maa');
  if (dropdown) {
    dropdown.addEventListener('click', (e) => e.stopPropagation());
  }
  const parentDiv = botao.parentElement;
  const quickActionsEl = parentDiv.nextElementSibling;
  const item = event.currentTarget.closest('.item');
  const copyAddressOption = item.querySelector('#quick-action-copy-address');
  const addToCompareOption = item.querySelector('#quick-action-add-to-compare');

  if (quickActionsEl && quickActionsEl.classList.contains("active")) {
    if (copyAddressOption) {
      copyAddressOption.removeEventListener('click', copyAddress);
    }
    if (addToCompareOption) {
      addToCompareOption.removeEventListener('click', addToCompareOptionClick)
    }
  } else {
    if (copyAddressOption) {
      copyAddressOption.addEventListener('click', copyAddress);
    }
    if (addToCompareOption) {
      addToCompareOption.addEventListener('click', addToCompareOptionClick)
    }
  }
};

export const debounce = (func, timeout = 1000) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

export const defaultSwiperOptions = {
  loop: true,
  pagination: {
    el: '.swiper-pagination',
    dynamicBullets: true,
    dynamicMainBullets: 3,
    clickable: true,
  },
  spaceBetween: 10,
}

export const communityCardClickHandler = (e) => {
  const link = e.currentTarget.querySelector('a.link');
  const target = e.target;
  const isActions = e.target.closest('.community-actions');
  const isSave = target.closest('a.apt-fav');
  const isPagination = target.closest('.swiper-pagination');
  if (isPagination || isActions || isSave) {
    return;
  }
  if (link) {
    window.open(link.getAttribute('href'), '_self');
  }
}

export const getViewComparisonLink = () => {
  const compareWidget = document.getElementById('compare-communities');
  const compareFooter = compareWidget && compareWidget.querySelector('.compare-communities__footer');
  const aTag = compareFooter && compareFooter.querySelector('a');
  if (aTag) {
    return aTag.href;
  }
}



// WEBPACK FOOTER //
// ./src/js/modules/utilities.js